<template>
  <Toast group="tr" />
  <ScrollTop
    :threshold="100"
    icon="pi pi-arrow-up"
    behavior="smooth"
    :buttonProps="{ severity: 'contrast', raised: true, rounded: true }"
  />
  <GlobalLoadingOverlay :show-label="true" />
  <Suspense>
    <template #default>
      <div class="flex flex-col gap-2">
        <NotificationAlert v-if="isNotificationsEnabled && isAuthenticated" />
        <TopNav v-if="isProdEnvironment || isAuthenticated" />
        <router-view />
      </div>
    </template>
    <template #fallback>
      <div class="text-sm text-gray-500 py-4">Loading...</div>
    </template>
  </Suspense>
  <ErrorDialog />
  <ConfirmDialog />
  <div
    class="text-xs font-italic my-5 text-center flex items-center justify-center gap-2"
    v-if="isProdEnvironment || isAuthenticated"
  >
    <router-link to="/release-notes">{{ versionLabel }}</router-link>
    <Button v-if="versionError" link size="small" @click="fetchVersion"> Retry </Button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount, onBeforeUnmount } from "vue";
import Toast from "primevue/toast";
import ScrollTop from "primevue/scrolltop";
import Button from "primevue/button";
import { useAuthToken } from "@/core/composables/useAuthToken";
import { usePermissions } from "@/core/composables/usePermissions";
import { useInfoApi } from "@/core/composables/useInfoApi";
import { TopNav, ErrorDialog } from "@/shared/components";
import GlobalLoadingOverlay from "./core/components/global/GlobalLoadingOverlay.vue";
import { useAppContextStore } from "@/core/store";
import { useEnvironmentFlags } from "@/core/composables/useEnvironmentFlags";
import { NotificationAlert } from "./features/notifications/components";

const { getTokenDuration } = useAuthToken();

const versionError = ref<string | null>(null);
let reloadTimer: number | undefined;

const { fetchVersion, version } = useInfoApi();
const contextStore = useAppContextStore();
const user = computed(() => contextStore.context.user);
const { isAuthenticated } = usePermissions(user);
const { notificationsEnabled, isProdEnvironment } = useEnvironmentFlags();

const isNotificationsEnabled = computed(() => notificationsEnabled.value);
const versionLabel = computed(() => version.value.version || "Release notes");

function scheduleTokenRefresh() {
  const tokenDuration = getTokenDuration();
  if (!Number.isFinite(tokenDuration) || tokenDuration <= 0) {
    return;
  }

  reloadTimer = window.setTimeout(() => {
    location.reload();
  }, tokenDuration);
}

onBeforeMount(() => {
  scheduleTokenRefresh();
  isAuthenticated.value && fetchVersion();
});

onBeforeUnmount(() => {
  if (reloadTimer) {
    clearTimeout(reloadTimer);
  }
});
</script>
